/**
 * Searchable PSA ticket property dropdown for the ticket thread properties panel.
 */
import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LayeredAutocompleteSelect, { type LayeredSelectOption } from "../LayeredAutocompleteSelect";
import { NEUTRAL, VIOLET } from "../../styles/brandTokens";
import { formatPropertyLabel } from "./propertyDots";

export type TicketPropertyField = "status" | "priority" | "queue" | "assignee" | "impact" | "client" | "requester";

const EDITABLE: Record<string, TicketPropertyField[]> = {
  autotask: ["status", "priority", "queue", "assignee", "client", "requester"],
  connectwise: ["status", "priority", "queue", "assignee", "impact", "client", "requester"],
  halo: ["status", "priority", "queue", "assignee", "client", "requester"],
};

const CURRENT = "__current__";
const FRESH_MS = 5 * 60 * 1000;

const propertyValueType = {
  fontFamily: "var(--theo-font-sans)",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.35,
  color: NEUTRAL.text,
};

export function canEditTicketProperty(platform: string | null | undefined, field: TicketPropertyField) {
  return EDITABLE[String(platform || "").toLowerCase()]?.includes(field) ?? false;
}

/** Impact is a ConnectWise ticket field. Autotask and Halo do not have it. */
export function showsTicketProperty(platform: string | null | undefined, field: TicketPropertyField) {
  if (field !== "impact") return true;
  return String(platform || "").toLowerCase() === "connectwise";
}

function message(error: unknown) {
  const data = (error as { response?: { data?: { error?: string; details?: string } } })?.response?.data;
  return data?.details || data?.error || (error instanceof Error ? error.message : "") || "Couldn't update the ticket";
}

function currentOption(label: string): LayeredSelectOption[] {
  return label ? [{ value: CURRENT, label }] : [];
}

interface TicketPropertySelectProps {
  ticketId: string;
  field: TicketPropertyField;
  ariaLabel: string;
  displayLabel: string;
  dotForLabel?: (label: string) => string;
  onUpdated: () => void;
}

const TicketPropertySelect: React.FC<TicketPropertySelectProps> = ({
  ticketId,
  field,
  ariaLabel,
  displayLabel,
  dotForLabel,
  onUpdated,
}) => {
  const api = useRef<{
    getTicketPropertyOptions: (ticketId: string, field: string, refresh?: boolean) => Promise<{ refresh?: boolean; freshForMs?: number; value?: string; options?: Array<{ id?: string | number; label?: string; searchText?: string }> }>;
    updateTicketProperty: (ticketId: string, field: string, value: string) => Promise<unknown>;
  } | null>(null);
  const request = useRef(0);
  const loadGeneration = useRef(0);
  const inFlight = useRef(false);
  const freshUntil = useRef(0);
  const picked = useRef(false);
  const valueRef = useRef(displayLabel ? CURRENT : "");
  const optionsRef = useRef<LayeredSelectOption[]>(currentOption(displayLabel));
  const client = async () => {
    if (!api.current) {
      const { default: SupervisorApi } = await import("../../apis/supervisorApi");
      api.current = new SupervisorApi();
    }
    return api.current;
  };
  const [options, setOptions] = useState<LayeredSelectOption[]>(() => currentOption(displayLabel));
  const [value, setValue] = useState(displayLabel ? CURRENT : "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    request.current += 1;
    loadGeneration.current += 1;
    picked.current = false;
    inFlight.current = false;
    freshUntil.current = 0;
    valueRef.current = displayLabel ? CURRENT : "";
    optionsRef.current = currentOption(displayLabel);
    setOptions(currentOption(displayLabel));
    setValue(displayLabel ? CURRENT : "");
    setError("");
    setLoading(false);
    setSaving(false);
  }, [ticketId, field]);

  useEffect(() => {
    if (value !== CURRENT) return;
    const placeholder = currentOption(displayLabel);
    optionsRef.current = placeholder;
    setOptions(placeholder);
    if (!displayLabel) setValue("");
  }, [displayLabel, value]);

  const applyOptions = (response: { value?: string; options?: Array<{ id?: string | number; label?: string; searchText?: string }> }, keepChoice: boolean) => {
    const format = field === "status" || field === "priority";
    const next = (Array.isArray(response?.options) ? response.options : []).map((option) => ({
      value: String(option?.id ?? ""),
      label: format ? formatPropertyLabel(option?.label) : String(option?.label || ""),
      ...(option?.searchText ? { searchText: option.searchText } : {}),
    })).filter((option) => option.value && option.label);
    if (keepChoice && picked.current && next.some((option) => option.value === valueRef.current)) {
      optionsRef.current = next;
      setOptions(next);
      return;
    }
    const selected = String(response?.value ?? "");
    if (selected && next.some((option) => option.value === selected)) {
      valueRef.current = selected;
      optionsRef.current = next;
      setOptions(next);
      setValue(selected);
      return;
    }
    const fallback = [...currentOption(displayLabel), ...next];
    valueRef.current = displayLabel ? CURRENT : "";
    optionsRef.current = fallback;
    setOptions(fallback);
    setValue(displayLabel ? CURRENT : "");
  };

  const rememberFresh = (freshForMs: unknown) => {
    const remaining = Number(freshForMs);
    freshUntil.current = Date.now() + (Number.isFinite(remaining) && remaining >= 0 ? remaining : FRESH_MS);
  };

  const load = async () => {
    const choices = optionsRef.current.some((option) => option.value && option.value !== CURRENT);
    if (choices && Date.now() < freshUntil.current) return;
    if (inFlight.current) return;
    const loadId = ++loadGeneration.current;
    inFlight.current = true;
    picked.current = false;
    setError("");
    const knownStale = choices && freshUntil.current > 0 && Date.now() >= freshUntil.current;
    if (!choices || knownStale) setLoading(true);
    try {
      const apiClient = await client();
      const first = await apiClient.getTicketPropertyOptions(ticketId, field);
      if (loadId !== loadGeneration.current) return;
      applyOptions(first, false);
      const remaining = Number(first.freshForMs);
      const stillFresh = Number.isFinite(remaining) ? remaining > 0 : !first.refresh;
      if (stillFresh) {
        rememberFresh(first.freshForMs);
        return;
      }
      setLoading(true);
      try {
        const fresh = await apiClient.getTicketPropertyOptions(ticketId, field, true);
        if (loadId !== loadGeneration.current) return;
        applyOptions(fresh, true);
        rememberFresh(fresh.freshForMs);
      } catch (refreshError) {
        if (loadId === loadGeneration.current) setError(message(refreshError));
      }
    } catch (loadError) {
      if (loadId !== loadGeneration.current) return;
      setError(message(loadError));
    } finally {
      if (loadId === loadGeneration.current) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  };

  const commit = async (next: string) => {
    if (!next || next === CURRENT || next === value) return;
    const requestId = ++request.current;
    picked.current = true;
    valueRef.current = next;
    const previousValue = value;
    const previousOptions = options;
    setValue(next);
    setSaving(true);
    setError("");
    try {
      await (await client()).updateTicketProperty(ticketId, field, next);
      onUpdated();
    } catch (saveError) {
      setValue(previousValue);
      setOptions(previousOptions);
      setError(message(saveError));
    } finally {
      if (requestId === request.current) setSaving(false);
    }
  };

  const shown = options.find((option) => option.value === value)?.label || displayLabel;

  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}>
      {dotForLabel && shown ? (
        <Box
          component="span"
          aria-hidden="true"
          sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, mt: "5px", backgroundColor: dotForLabel(shown) }}
        />
      ) : null}
      {/* Ticket property value. Closed state is wrapping text; the combobox sits on top of it so the narrow column is not eaten by input padding. */}
      <Box
        className="ticket-property-field"
        onClick={(event) => {
          const input = event.currentTarget.querySelector("input");
          if (input && document.activeElement !== input) input.focus();
        }}
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          cursor: "pointer",
          "&:hover .ticket-property-chevron": { opacity: loading || saving ? 0 : 0.85 },
        }}
      >
        <Typography
          aria-hidden
          className="ticket-property-value"
          sx={{
            visibility: open ? "hidden" : "visible",
            ...propertyValueType,
            color: shown ? NEUTRAL.text : NEUTRAL.textMuted,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            overflowWrap: "anywhere",
            minHeight: 18,
            pr: loading || saving ? "18px" : 0,
          }}
        >
          {shown || "Select"}
        </Typography>
        <KeyboardArrowDownIcon
          aria-hidden
          className="ticket-property-chevron"
          sx={{
            position: "absolute",
            right: 0,
            top: 1,
            fontSize: 14,
            color: NEUTRAL.textMuted,
            pointerEvents: "none",
            opacity: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: open ? 1 : 0,
            overflow: open ? "visible" : "hidden",
            bgcolor: open ? "#FFFFFF" : "transparent",
          }}
        >
          <LayeredAutocompleteSelect
            label=""
            inputAriaLabel={ariaLabel}
            value={value}
            options={options}
            onOpen={() => { setOpen(true); void load(); }}
            onClose={() => setOpen(false)}
            onChange={(next) => { void commit(next); }}
            disabled={saving}
            disableClearable
            size="small"
            placeholder="Select"
            inputTitle={shown || undefined}
            error={Boolean(error)}
            helperText={open ? error || undefined : undefined}
            listboxMaxHeight={280}
            menuMinWidth={220}
            optionSx={propertyValueType}
            sx={{
              minWidth: 0,
              height: "100%",
              "& .MuiAutocomplete-endAdornment": { display: "none" },
            }}
            textFieldSx={{
              height: "100%",
              "& .MuiInputBase-root": {
                fontSize: 13,
                fontWeight: 500,
                color: NEUTRAL.text,
                background: "transparent",
                padding: "0 !important",
                minHeight: 0,
                height: "100%",
                cursor: open ? "text" : "pointer",
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: open ? NEUTRAL.borderStrong : "transparent" },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: open ? VIOLET.main : "transparent" },
              "& .MuiAutocomplete-input": {
                padding: "0 !important",
                fontFamily: "var(--theo-font-sans)",
                fontSize: "13px !important",
                fontWeight: 500,
                lineHeight: 1.35,
                width: "100% !important",
                minWidth: "0 !important",
                opacity: "1 !important",
                ...(loading || saving ? { paddingRight: "18px !important" } : {}),
              },
            }}
          />
        </Box>
        {/* Single catalog spinner. Hidden once the cached list is under five minutes old. */}
        {loading || saving ? (
          <CircularProgress size={12} aria-label="Loading choices" sx={{ position: "absolute", right: 0, top: 3, zIndex: 1, pointerEvents: "none" }} />
        ) : null}
      </Box>
    </Box>
      {error && !open ? (
        <Typography sx={{ color: "#B42318", fontSize: 11, fontFamily: "var(--theo-font-sans)", mt: 0.25 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default TicketPropertySelect;
